import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { Club } from '../../types';
import './ClubCreateModal.css';

interface ClubCreateModalProps {
  onClose: () => void;
  onCreated: (club: Club) => void;
}

const ClubCreateModal: React.FC<ClubCreateModalProps> = ({ onClose, onCreated }) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [description, setDescription] = useState('');
  const [timezone, setTimezone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'Europe/Zurich'; }
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !shortName.trim()) return;

    setCreating(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), short_name: shortName.trim(), description: description.trim() || undefined, timezone }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('clubs.errorCreate'));
      }

      const data = await res.json();
      onCreated(data.club);
    } catch (err: any) {
      setError(err.message || t('clubs.errorCreate'));
    } finally {
      setCreating(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="club-modal-overlay" onClick={handleOverlayClick}>
      <div className="club-modal-card">
        <div className="club-modal-header">
          <h2 className="club-modal-title">{t('clubs.createTitle')}</h2>
          <button className="club-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <form className="club-modal-form" onSubmit={handleSubmit}>
          <div className="club-modal-field">
            <label htmlFor="club-name" className="club-modal-label">{t('clubs.clubName')} <span className="club-modal-required">*</span></label>
            <input
              id="club-name"
              type="text"
              className="club-modal-input"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              maxLength={100}
              autoFocus
            />
          </div>

          <div className="club-modal-field">
            <label htmlFor="club-short-name" className="club-modal-label">{t('clubs.shortName')} <span className="club-modal-required">*</span></label>
            <input
              id="club-short-name"
              type="text"
              className="club-modal-input"
              value={shortName}
              onChange={e => setShortName(e.target.value)}
              required
              maxLength={10}
              placeholder="e.g. AEC"
            />
          </div>

          <div className="club-modal-field">
            <label htmlFor="club-timezone" className="club-modal-label">{t('clubs.timezone') || 'Timezone'}</label>
            <select
              id="club-timezone"
              className="club-modal-input"
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
            >
              {['America/Mexico_City','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Sao_Paulo','America/Argentina/Buenos_Aires','Europe/London','Europe/Paris','Europe/Berlin','Europe/Zurich','Europe/Warsaw','Europe/Madrid','Europe/Rome','Europe/Moscow','Asia/Tokyo','Asia/Shanghai','Asia/Kolkata','Asia/Dubai','Australia/Sydney','Pacific/Auckland'].map(tz => (
                <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div className="club-modal-field">
            <label htmlFor="club-description" className="club-modal-label">{t('clubs.clubDescription')}</label>
            <textarea
              id="club-description"
              className="club-modal-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="club-modal-hint"><span className="club-modal-required">*</span> {t('common.required') || 'Required'}</div>

          {error && <div className="club-modal-error">{error}</div>}

          <div className="club-modal-actions">
            <button
              type="button"
              className="club-modal-btn club-modal-btn-cancel"
              onClick={onClose}
              disabled={creating}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="club-modal-btn club-modal-btn-submit"
              disabled={!name.trim() || !shortName.trim() || creating}
            >
              {creating ? t('clubs.creating') : t('clubs.createClub')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClubCreateModal;
